/**
 * ⭐⭐ Board 2638 (Alex, 2026-09-30: "not reliant on their PCs clocks") — THE ONE
 * PLACE A PORTAL WALL CLOCK BECOMES AN INSTANT, AND BACK, IN `Europe/Dublin`.
 *
 * ⛔ THE ZONE IS PINNED, NEVER THE DEVICE'S. What a nurse types is Irish time
 * (the business, the homes and every booking are in Ireland; CLAUDE.md pins
 * `Europe/Dublin` for every booking date), so the instant must not depend on
 * how the ward PC or a phone happens to be set. (What this supersedes is
 * recorded once, on `derivePickup` in `client/request-form-logic.ts`.)
 *
 * ⛔ PURE AND DEPENDENCY-FREE: `shared/request-schema.ts` imports it, and that
 * module bundles into the browser. `Intl` is the whole engine; no tz library.
 *
 * ⚠️ NO `padStart` IN THIS FILE, BY DESIGN: every two-digit field is read off
 * the formatter's own `2-digit` parts, and quote-helper's job-reference census
 * (`test/core/job-ref-parity.spec.ts`) reds on a two-digit pad in the portal.
 */

const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;
const TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;
const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * ⛔ ONE MODULE-LEVEL FORMATTER, `hourCycle: 'h23'` AND NO `hour12`. `hour12:
 * false` has rendered midnight as `24` on some engines, so the hour cycle is
 * named instead, and `24` is still read as `0` below in case an engine ignores it.
 */
const DUBLIN = new Intl.DateTimeFormat('en-GB', {
  timeZone: 'Europe/Dublin',
  hourCycle: 'h23',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  second: '2-digit',
});

type WallParts = { year: string; month: string; day: string; hour: string; minute: string; second: string };

function wallPartsAt(ms: number): WallParts {
  const parts: WallParts = { year: '', month: '', day: '', hour: '', minute: '', second: '' };
  for (const p of DUBLIN.formatToParts(ms)) {
    if (p.type in parts) parts[p.type as keyof WallParts] = p.value;
  }
  if (parts.hour === '24') parts.hour = '00';
  return parts;
}

/** Dublin's offset from UTC at `ms`, in minutes (60 in IST, 0 in GMT): the wall
 *  reading taken as if it were UTC, minus the instant (floored to the second,
 *  because the formatter has no milliseconds). */
function offsetMinutesAt(ms: number): number {
  const p = wallPartsAt(ms);
  const wall = Date.UTC(
    Number(p.year),
    Number(p.month) - 1,
    Number(p.day),
    Number(p.hour),
    Number(p.minute),
    Number(p.second),
  );
  return (wall - Math.floor(ms / 1000) * 1000) / 60_000;
}

/**
 * The instant a Dublin wall clock names, as canonical ISO, or null.
 *
 * ⛔ STRICT INPUT: `YYYY-MM-DD` and 24-hour `HH:MM` only, and a day that does not
 * survive a calendar round trip is REFUSED (`2026-02-31`, `2026-09-31`), where
 * `new Date` would roll it into the next month and book a day nobody typed.
 *
 * ⛔ THE TWO DST EDGES, ONE POLICY (plan D4):
 *   · a REPEATED time (the last Sunday in October, 01:00-01:59) means the
 *     EARLIER occurrence, IST: the ECMA-262 / Temporal `compatible` choice, and
 *     lead-time safe (a bus an hour early, never an hour late);
 *   · a MISSING time (the last Sunday in March, 01:00-01:59) moves FORWARD,
 *     `01:30` is stored as 02:30 IST, as V8's own `Date` does.
 * The candidates are the offsets in force a day either side, which are the only
 * two a transition can be between; a candidate is kept when the offset in force
 * AT it agrees with the one used to reach it.
 */
export function dublinInstant(day: string, time: string): string | null {
  if (typeof day !== 'string' || !DAY_RE.test(day)) return null;
  if (typeof time !== 'string' || !TIME_RE.test(time)) return null;
  const y = Number(day.slice(0, 4));
  const m = Number(day.slice(5, 7));
  const d = Number(day.slice(8, 10));
  const wall = Date.UTC(y, m - 1, d, Number(time.slice(0, 2)), Number(time.slice(3, 5)));
  const check = new Date(wall);
  if (check.getUTCFullYear() !== y || check.getUTCMonth() !== m - 1 || check.getUTCDate() !== d) {
    return null;
  }
  const offsetBefore = offsetMinutesAt(wall - DAY_MS);
  const offsetAfter = offsetMinutesAt(wall + DAY_MS);
  const agreeing = [...new Set([offsetBefore, offsetAfter])]
    .map((off) => ({ off, ms: wall - off * 60_000 }))
    .filter(({ off, ms }) => offsetMinutesAt(ms) === off)
    .map(({ ms }) => ms);
  const ms = agreeing.length > 0 ? Math.min(...agreeing) : wall - offsetBefore * 60_000;
  return new Date(ms).toISOString();
}

/**
 * The Dublin wall clock at an instant: `{ day: 'YYYY-MM-DD', time: 'HH:MM' }`,
 * or null when the instant is not a valid date. The inverse the form's
 * Collection preview and the return-time route read the journey's day with.
 */
export function dublinWallClock(instant: string | Date): { day: string; time: string } | null {
  const ms = (instant instanceof Date ? instant : new Date(instant)).getTime();
  if (Number.isNaN(ms)) return null;
  const p = wallPartsAt(ms);
  return { day: `${p.year}-${p.month}-${p.day}`, time: `${p.hour}:${p.minute}` };
}
